// Daily race prizes: amounts are kept in kopecks, entered in rubles with an optional comma.
export const DAILY_RACE_PLACES = 100;

// The script prizes round to 10 RUB, the same as Selector-Core calculatePrize.
const SCRIPT_ROUNDING_KOPECKS = 1000;
const FIXED_PERCENTAGES = [25, 12, 8, 6, 5, 3.5, 2.5, 2, 1.5, 1];
const DECAY_FACTOR = 0.8;

export const parseRubToKopecks = (value) => {
	const normalized = String(value === null || typeof value === "undefined" ? "" : value)
		.trim()
		.replace(/\s/g, "")
		.replace(",", ".");
	if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;

	return Math.round(Number(normalized) * 100);
};

export const formatKopecksInput = (kopecks) => {
	const number = Number(kopecks);
	if (!Number.isFinite(number)) return "";

	const rubles = Math.floor(number / 100);
	const rest = Math.round(number % 100);

	return rest ? `${rubles},${String(rest).padStart(2, "0")}` : String(rubles);
};

export const formatKopecks = (kopecks) =>
	`${new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(kopecks || 0) / 100)} RUB`;

// Share of the pool for every place: top 10 are fixed, the rest decay to 0.8 of place 11.
const getScriptShares = () => {
	const fixedTotal = FIXED_PERCENTAGES.reduce((sum, percent) => sum + percent, 0) / 100;
	const remainingPlaces = DAILY_RACE_PLACES - FIXED_PERCENTAGES.length;
	const factors = Array.from({ length: remainingPlaces }, (_, index) => Math.pow(DECAY_FACTOR, index / (remainingPlaces - 1)));
	const factorsTotal = factors.reduce((sum, factor) => sum + factor, 0);

	return [
		...FIXED_PERCENTAGES.map((percent) => percent / 100),
		...factors.map((factor) => ((1 - fixedTotal) * factor) / factorsTotal),
	];
};

const roundScriptPrize = (kopecks) => Math.round(kopecks / SCRIPT_ROUNDING_KOPECKS) * SCRIPT_ROUNDING_KOPECKS;

// Rounding up can push the total over the limit; the excess is taken from the last places.
const trimToLimit = (prizes, indexes, limit) => {
	const result = [...prizes];
	let excess = indexes.reduce((sum, index) => sum + result[index], 0) - limit;

	for (let position = indexes.length - 1; position >= 0 && excess > 0; position -= 1) {
		const index = indexes[position];
		const cut = Math.min(result[index], Math.ceil(excess / SCRIPT_ROUNDING_KOPECKS) * SCRIPT_ROUNDING_KOPECKS);
		result[index] -= cut;
		excess -= cut;
	}

	return result;
};

// Splits the whole pool between all places.
export const distributeAllPrizes = (poolKopecks) => {
	const shares = getScriptShares();
	const prizes = shares.map((share) => roundScriptPrize(poolKopecks * share));

	return trimToLimit(prizes, prizes.map((_, index) => index), poolKopecks);
};

// Splits what is left of the pool between the empty places only; filled places stay as they are.
export const distributeEmptyPrizes = (poolKopecks, currentPrizes) => {
	const filledTotal = currentPrizes.reduce((sum, prize) => sum + (prize === null ? 0 : prize), 0);
	const remainder = poolKopecks - filledTotal;
	const emptyIndexes = currentPrizes.map((prize, index) => (prize === null ? index : null)).filter((index) => index !== null);

	if (!emptyIndexes.length || remainder <= 0) return { prizes: currentPrizes, remainder };

	const shares = getScriptShares();
	const emptySharesTotal = emptyIndexes.reduce((sum, index) => sum + shares[index], 0);
	const prizes = [...currentPrizes];

	emptyIndexes.forEach((index) => {
		prizes[index] = roundScriptPrize((remainder * shares[index]) / emptySharesTotal);
	});

	return { prizes: trimToLimit(prizes, emptyIndexes, remainder), remainder };
};

// Always 100 rows; places without a saved prize stay empty.
export const createDailyRacePrizeRows = (distribution = []) =>
	Array.from({ length: DAILY_RACE_PLACES }, (_, index) => {
		const saved = (Array.isArray(distribution) ? distribution : []).find((item) => Number(item?.place) === index + 1);

		return { place: index + 1, prize: saved && saved.prize !== null && saved.prize !== "" && typeof saved.prize !== "undefined" ? String(saved.prize) : "" };
	});
